import { Alert, Button, Loader, Modal, ScrollArea } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { observer } from 'mobx-react';
import { t } from 'i18next';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { IconAlertCircle, IconArrowRight, IconCheck, IconLanguage, IconLock, IconShieldCheck } from '@tabler/icons-react';
import { ILegalRequirement, resolveLegalContentUrl } from '../../../entities/Legal/Legal.ts';
import { usePostAcceptLegalDocument } from '../../../entities/Legal/mutations/usePostAcceptLegalDocument.tsx';
import { usePostSetLanguageMutation } from '../../../entities/Login/mutations/usePostSetLanguageMutation.tsx';
import styles from './LegalAcceptanceWizard.module.css';

interface LegalAcceptanceWizardProps {
    documents: ILegalRequirement[];
    onCompleted: () => void;
}

const actionLabelKey: Record<string, string> = {
    accept: 'legalAcceptance.actions.accept',
    acknowledge: 'legalAcceptance.actions.acknowledge',
    consent: 'legalAcceptance.actions.consent',
};

interface LegalDocumentStepProps {
    document: ILegalRequirement;
    onScroll: (percent: number, atBottom: boolean) => void;
}

const LegalDocumentStep: React.FC<LegalDocumentStepProps> = observer(({ document, onScroll }) => {
    const { i18n } = useTranslation();
    const [markdown, setMarkdown] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [atBottom, setAtBottom] = useState(false);
    const scrollAreaRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const loadMarkdown = async () => {
            setIsLoading(true);
            setLoadError(false);
            setAtBottom(false);
            try {
                const url = resolveLegalContentUrl(document.contentPathTemplate, i18n.language);
                const res = await fetch(url);
                if (!res.ok) throw new Error(`Failed to fetch document: ${res.status}`);
                setMarkdown(await res.text());
            } catch (error) {
                console.error('Error loading legal document:', error);
                setLoadError(true);
                setMarkdown('');
            } finally {
                setIsLoading(false);
            }
        };

        loadMarkdown();
    }, [document.contentPathTemplate, i18n.language]);

    useEffect(() => {
        if (!scrollAreaRef.current) return;

        const selectors = [
            '[data-radix-scroll-area-viewport]',
            '.mantine-ScrollArea-viewport',
            '[data-mantine-scroll-area-viewport]',
            'div[style*="overflow"]',
        ];

        let scrollElement: HTMLElement | null = null;
        for (const selector of selectors) {
            scrollElement = scrollAreaRef.current.querySelector(selector);
            if (scrollElement) break;
        }
        if (!scrollElement) return;

        const el = scrollElement;
        const handleScrollEvent = () => {
            const { scrollTop, scrollHeight, clientHeight } = el;
            const scrollable = Math.max(scrollHeight - clientHeight, 0);
            const percent = scrollable === 0 ? 100 : Math.min(100, (scrollTop / scrollable) * 100);
            const reachedBottom = scrollTop + clientHeight >= scrollHeight - 20;
            setAtBottom(reachedBottom);
            onScroll(percent, reachedBottom);
        };

        el.addEventListener('scroll', handleScrollEvent);
        const initialCheck = setTimeout(handleScrollEvent, 400);

        return () => {
            el.removeEventListener('scroll', handleScrollEvent);
            clearTimeout(initialCheck);
        };
    }, [markdown, onScroll]);

    return (
        <div className={styles.readerWrap}>
            {loadError ? (
                <Alert className={styles.alert} icon={<IconAlertCircle size="1rem" />} color="red">
                    {t('legalAcceptance.loadError')}
                </Alert>
            ) : isLoading ? (
                <div className={styles.loading}>
                    <Loader size="lg" />
                </div>
            ) : (
                <>
                    <ScrollArea className={styles.scroll} ref={scrollAreaRef} scrollbarSize={8}>
                        <div className={styles.prose}>
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                rehypePlugins={[rehypeRaw]}
                                skipHtml={false}
                                components={{
                                    table: (props) => <table className="markdown-table" {...props} />,
                                    th: (props) => <th className="markdown-th" {...props} />,
                                    td: (props) => <td className="markdown-td" {...props} />,
                                }}
                            >
                                {markdown}
                            </ReactMarkdown>
                        </div>
                    </ScrollArea>
                    <div className={styles.fade} data-hidden={atBottom} />
                </>
            )}
        </div>
    );
});

export const LegalAcceptanceWizard: React.FC<LegalAcceptanceWizardProps> = observer(({ documents, onCompleted }) => {
    const { i18n } = useTranslation();
    const acceptMutation = usePostAcceptLegalDocument();
    const setLanguageMutation = usePostSetLanguageMutation();
    const [isSwitchingLanguage, setIsSwitchingLanguage] = useState(false);

    const firstPending = useMemo(() => {
        const index = documents.findIndex((doc) => !doc.accepted);
        return index < 0 ? 0 : index;
    }, [documents]);

    const [active, setActive] = useState(firstPending);
    const [scrollPct, setScrollPct] = useState(0);
    const [hasRead, setHasRead] = useState(false);

    if (documents.length === 0) return null;

    const current = documents[Math.min(active, documents.length - 1)];

    const handleScroll = useCallback((percent: number, atBottom: boolean) => {
        setScrollPct(percent);
        if (atBottom) setHasRead(true);
    }, []);

    const handleAccept = async () => {
        try {
            await acceptMutation.mutateAsync({
                documentType: current.documentType,
                version: current.version,
                language: i18n.language,
            });

            if (active >= documents.length - 1) {
                onCompleted();
                return;
            }
            setActive((a) => a + 1);
            setScrollPct(0);
            setHasRead(false);
        } catch (error) {
            console.error('Failed to accept legal document:', error);
        }
    };

    const isFrench = i18n.language === 'fr';

    // Persist the language like the platform header does; if persistence fails,
    // still switch the UI locally so the reader is never stuck in a language
    // the user can't follow.
    const handleSelectLanguage = async (language: 'en' | 'fr') => {
        if (language === (isFrench ? 'fr' : 'en')) return;
        setIsSwitchingLanguage(true);
        try {
            await setLanguageMutation.mutateAsync({ language });
        } catch (error) {
            console.error('Failed to persist language preference:', error);
            await i18n.changeLanguage(language);
        } finally {
            setIsSwitchingLanguage(false);
        }
    };

    const isLastStep = active >= documents.length - 1;

    return (
        <Modal
            opened
            onClose={() => {}}
            size={780}
            centered
            padding={0}
            radius="lg"
            closeOnClickOutside={false}
            closeOnEscape={false}
            withCloseButton={false}
            zIndex={1000}
            withinPortal
            classNames={{ content: styles.modalContent }}
            styles={{ content: { padding: 0 }, body: { padding: 0 } }}
        >
            <div className={styles.wizard}>
                <div className={styles.header}>
                    <div className={styles.headerTop}>
                        <div className={styles.eyebrow}>
                            <span className={styles.eyebrowDot} />
                            {t('legalAcceptance.eyebrow')}
                        </div>

                        <div
                            className={styles.langSwitch}
                            role="group"
                            aria-label={t('navbar.changeLanguage')}
                            data-busy={isSwitchingLanguage}
                        >
                            <IconLanguage size={15} className={styles.langIcon} />
                            <button
                                type="button"
                                className={styles.langOption}
                                data-active={!isFrench}
                                aria-pressed={!isFrench}
                                disabled={isSwitchingLanguage}
                                onClick={() => handleSelectLanguage('en')}
                            >
                                EN
                            </button>
                            <button
                                type="button"
                                className={styles.langOption}
                                data-active={isFrench}
                                aria-pressed={isFrench}
                                disabled={isSwitchingLanguage}
                                onClick={() => handleSelectLanguage('fr')}
                            >
                                FR
                            </button>
                        </div>
                    </div>

                    <div className={styles.rail}>
                        {documents.map((doc, index) => {
                            const state =
                                index < active ? 'done' : index === active ? 'current' : 'upcoming';
                            const fill = index < active ? 100 : index === active ? scrollPct : 0;
                            return (
                                <div className={styles.segment} key={doc.documentType} data-state={state}>
                                    <div className={styles.segmentBar}>
                                        <span className={styles.segmentBarFill} style={{ width: `${fill}%` }} />
                                    </div>
                                    <span className={styles.segmentLabel}>
                                        <span className={styles.segmentIndex}>
                                            {state === 'done' ? <IconCheck size={11} stroke={3} /> : index + 1}
                                        </span>
                                        {t(`legalAcceptance.documents.${doc.documentType}`)}
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    <div className={styles.titleRow}>
                        <h2 className={styles.title}>
                            {t(`legalAcceptance.documents.${current.documentType}`)}
                        </h2>
                        <div className={styles.meta}>
                            {t('legalAcceptance.stepProgress', {
                                current: active + 1,
                                total: documents.length,
                            })}
                            <br />
                            {t('legalAcceptance.version', { version: current.version })}
                        </div>
                    </div>
                    <p className={styles.instruction}>{t('legalAcceptance.instruction')}</p>
                </div>

                <LegalDocumentStep key={current.documentType} document={current} onScroll={handleScroll} />

                <div className={styles.footer}>
                    <div className={styles.status} data-read={hasRead}>
                        <span className={styles.statusIcon}>
                            {hasRead ? <IconCheck size={14} stroke={2.5} /> : <IconLock size={13} />}
                        </span>
                        {hasRead ? t('legalAcceptance.scrollComplete') : t('legalAcceptance.scrollRequired')}
                    </div>

                    <Button
                        className={styles.acceptButton}
                        onClick={handleAccept}
                        disabled={!hasRead || acceptMutation.isPending}
                        loading={acceptMutation.isPending}
                        size="md"
                        radius="md"
                        rightSection={
                            isLastStep ? <IconShieldCheck size={18} /> : <IconArrowRight size={18} />
                        }
                    >
                        {isLastStep
                            ? t('legalAcceptance.finish')
                            : t(actionLabelKey[current.action] ?? actionLabelKey.accept)}
                    </Button>
                </div>
            </div>
        </Modal>
    );
});

export default LegalAcceptanceWizard;
